import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * QuickActionModal - Generic modal for simple inventory actions
 * Handles Promote, Verify, Order Certificate, and Analyze
 */
import { useState } from 'react';
import styled from 'styled-components';
import BaseModal from '../shared/BaseModal';
import { notify } from '../../utils/notifications';
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
  text-align: center;
`;
const DiamondPreview = styled.div `
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--text-primary);
    font-size: 1.25rem;
  }

  .specs {
    color: var(--text-secondary);
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .price {
    margin-top: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
    color: #8b5cf6;
    font-family: 'JetBrains Mono', monospace;
  }
`;
const FormGroup = styled.div `
  margin-bottom: 1rem;
`;
const Label = styled.label `
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;
const TextArea = styled.textarea `
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
`;
const Button = styled.button `
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.primary ? '#8b5cf6' : 'var(--border-color)'};
  background: ${props => props.primary ? '#8b5cf6' : 'var(--bg-secondary)'};
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const CalculationBox = styled.div `
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05));
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
`;
const CalcRow = styled.div `
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: var(--text-secondary);

  &:not(:last-child) {
    border-bottom: 1px solid rgba(139, 92, 246, 0.2);
  }

  &:last-child {
    font-weight: 700;
    color: var(--text-primary);
  }
`;
export const QuickActionModal = ({ isOpen, onClose, diamond, actionType, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [notes, setNotes] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const handleSubmit = async () => {
        if (!diamond)
            return;
        setLoading(true);
        try {
            // TODO: Call appropriate API based on actionType
            // For now, simulate success
            const messages = {
                'promote': `Promotion campaign created for ${diamond.stockNumber}`,
                'verify': `Verification request submitted for ${diamond.stockNumber}`,
                'order-cert': `Certificate order placed for ${diamond.stockNumber}`,
                'analyze': `Margin analysis completed for ${diamond.stockNumber}`
            };
            notify.success('Success', messages[actionType]);
            onSuccess?.();
            onClose();
        }
        catch (error) {
            console.error(`Action ${actionType} failed:`, error);
            notify.error('Error', 'Failed to complete action');
        }
        finally {
            setLoading(false);
        }
    };
    if (!diamond)
        return null;
    const getTitle = () => {
        switch (actionType) {
            case 'promote': return 'Promote Diamond';
            case 'verify': return 'Verify Diamond';
            case 'order-cert': return 'Order Certificate';
            case 'analyze': return 'Analyze Margin';
        }
    };
    const getContent = () => {
        switch (actionType) {
            case 'promote':
                return (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Promotion Channel *" }), _jsxs(Select, { value: selectedOption, onChange: (e) => setSelectedOption(e.target.value), children: [_jsx("option", { value: "", children: "Select channel..." }), _jsx("option", { value: "email", children: "Email Campaign" }), _jsx("option", { value: "social", children: "Social Media" }), _jsx("option", { value: "website", children: "Website Feature" }), _jsx("option", { value: "dealer", children: "Dealer Network" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Campaign Notes" }), _jsx(TextArea, { value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Add marketing notes, target audience, special messaging..." })] })] }));
            case 'verify':
                return (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Verification Type *" }), _jsxs(Select, { value: selectedOption, onChange: (e) => setSelectedOption(e.target.value), children: [_jsx("option", { value: "", children: "Select verification..." }), _jsx("option", { value: "authenticity", children: "Authenticity Check" }), _jsx("option", { value: "condition", children: "Condition Inspection" }), _jsx("option", { value: "appraisal", children: "Re-Appraisal" }), _jsx("option", { value: "insurance", children: "Insurance Valuation" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Notes" }), _jsx(TextArea, { value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Add verification notes, concerns, or special instructions..." })] })] }));
            case 'order-cert':
                return (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Laboratory *" }), _jsxs(Select, { value: selectedOption, onChange: (e) => setSelectedOption(e.target.value), children: [_jsx("option", { value: "", children: "Select lab..." }), _jsx("option", { value: "GIA", children: "GIA (Gemological Institute of America)" }), _jsx("option", { value: "AGS", children: "AGS (American Gem Society)" }), _jsx("option", { value: "IGI", children: "IGI (International Gemological Institute)" }), _jsx("option", { value: "HRD", children: "HRD Antwerp" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Special Instructions" }), _jsx(TextArea, { value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Rush service, specific grading requests, return shipping preferences..." })] })] }));
            case 'analyze':
                return (_jsxs(_Fragment, { children: [diamond.costPerCarat ? (_jsxs(CalculationBox, { children: [_jsxs(CalcRow, { children: [_jsx("span", { children: "Cost Per Carat:" }), _jsxs("strong", { children: ["$", diamond.costPerCarat.toLocaleString()] })] }), _jsxs(CalcRow, { children: [_jsx("span", { children: "Price Per Carat:" }), _jsxs("strong", { children: ["$", diamond.pricePerCarat.toLocaleString()] })] }), _jsxs(CalcRow, { children: [_jsx("span", { children: "Margin:" }), _jsxs("strong", { style: { color: ((diamond.pricePerCarat - diamond.costPerCarat) / diamond.costPerCarat * 100) >= 20 ? '#10b981' : '#ef4444' }, children: [((diamond.pricePerCarat - diamond.costPerCarat) / diamond.costPerCarat * 100).toFixed(1), "%"] })] }), _jsxs(CalcRow, { children: [_jsx("span", { children: "Profit:" }), _jsxs("strong", { children: ["$", ((diamond.pricePerCarat - diamond.costPerCarat) * diamond.caratWeight).toLocaleString()] })] })] })) : (_jsx("div", { style: { padding: '1rem', textAlign: 'center', color: 'var(--text-tertiary)' }, children: "No cost data available for margin analysis" })), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Analysis Notes" }), _jsx(TextArea, { value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Add pricing strategy notes, market comparison, recommendations..." })] })] }));
        }
    };
    return (_jsxs(BaseModal, { isOpen: isOpen, onClose: onClose, title: getTitle(), maxWidth: "600px", footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: onClose, children: "Cancel" }), _jsx(Button, { primary: true, onClick: handleSubmit, disabled: loading || (actionType !== 'analyze' && !selectedOption), children: loading ? 'Processing...' : 'Confirm' })] }), children: [_jsx(InfoBox, { children: _jsxs(DiamondPreview, { children: [_jsxs("h3", { children: [diamond.caratWeight, "ct ", diamond.shape] }), _jsxs("div", { className: "specs", children: ["Color: ", diamond.color, " \u2022 Clarity: ", diamond.clarity, " \u2022 Cut: ", diamond.cut, _jsx("br", {}), "Lab: ", diamond.lab, diamond.certificateNumber && ` • Cert #${diamond.certificateNumber}`, diamond.stockNumber && _jsx("br", {}), diamond.stockNumber && `Stock #${diamond.stockNumber}`] }), _jsxs("div", { className: "price", children: ["$", diamond.totalPrice.toLocaleString()] })] }) }), getContent()] }));
};
